import { useContext, type JSX } from 'react';
import AnalysListQuery from './AnalysListQuery.gql';
import {
  AnalysListQuery as IData,
  AnalysListQueryVariables as IVariables,
} from './_QueryTypes/AnalysListQuery';
import { Query } from '@apollo/client/react/components';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import { getGbsRoles } from '../../../Analysutdelning/Components/AnalysUtdelningTable/Utils/AnalysUtdelningTableUtils';
import { IGBSRole } from '../../../Analysutdelning/Queries/AnalysutdelningQuery/AnalysutdelningQuery';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../Utils/mergeSort';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

export interface IAnalys {
  id: string; // Analysens id
  idkod?: string; // Analysens idkod om standard rapport
  name: string; // Analysens namn
  baseSelection: IBaseSelection;
  favoritLopnr: number | null;
  utdelatLopnr: number | null;
  kategori: string;
  kategori_ID: number | undefined;
  defaultFromYear?: number;
  defaultFromPeriod?: number;
  obligatorisk: boolean;
  obligatoriskRoller: IGBSRole[];
  detaljanalys: boolean;
  ai_prompt: string | null;
}

export interface ISelection {
  id: string; // Kodbehörighet id ()
  name: string; // Kodbehörighetens namn
  anvandsFor: string | null;
  kombinerad: boolean;
  analysList: IAnalys[]; // Lista med Analyser
}

interface IBaseSelection {
  id?: string;
  name?: string;
}

interface IProps {
  codeSystemId: string;
  onlyFavorites?: boolean;
  children: (
    groupedReports: ISelection[],
    loading: boolean,
    hasData: boolean
  ) => JSX.Element | null;
}

const isObligatorisk = (
  myGbsRoles: IGBSRole[],
  obligatoriskGasRoles: string
) => {
  const obligatoriskForRoleCodes = obligatoriskGasRoles
    .split(',')
    .map(role => role.trim());

  return obligatoriskForRoleCodes.some(obligatoriskForRoleCode =>
    myGbsRoles.some(myGbsRole => myGbsRole.code === obligatoriskForRoleCode)
  );
};

const EkonomiskAnalysListQuery = (props: IProps) => {
  const { onlyFavorites = false, codeSystemId } = props;

  const kodplanContext = useContext(KodplanContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  return (
    <Query<IData, IVariables>
      query={AnalysListQuery}
      fetchPolicy="cache-first"
      variables={{
        codeSystemId: codeSystemId,
        onlyFavorites: false,
        dbName: activeDatabase ?? '',
      }}
      skip={!activeDatabase}
    >
      {({ loading, error, data }) => {
        let selections: ISelection[] = [];

        if (error) {
          throw new Error(error.message);
        }

        if (!error && data) {
          if (data.ekkodbehmedanalyser.kodbehorigheter) {
            const codePrivileges = data.ekkodbehmedanalyser.kodbehorigheter;

            selections = codePrivileges.map((codePrivilege): ISelection => {
              const utdeladeAnalyser = codePrivilege.utdeladeanalyser;

              return {
                id: codePrivilege.kodbeh_id,
                name: codePrivilege.name,
                anvandsFor: codePrivilege.anvands_for,
                kombinerad: codePrivilege.kombinerad ?? false,
                analysList: !utdeladeAnalyser
                  ? []
                  : utdeladeAnalyser
                      .filter(
                        analys => !onlyFavorites || analys.favorit_lopnr != null
                      )
                      .map((analys): IAnalys => {
                        return {
                          id: analys.analys_id,
                          idkod: analys.idkod ? analys.idkod : undefined,
                          name: analys.name,
                          favoritLopnr: analys.favorit_lopnr,
                          utdelatLopnr: analys.utdelning_lopnr,
                          kategori: analys.Kategori || '',
                          kategori_ID: analys.kategori_id || undefined,
                          obligatorisk: kodplanContext.activeKodplan?.gas
                            ? isObligatorisk(
                                data?.me?.[0]?.roles ?? [],
                                analys.obligatorisk_gas ?? ''
                              )
                            : analys.obligatorisk_ej_gas,
                          obligatoriskRoller: analys.obligatorisk_gas
                            ? getGbsRoles(
                                analys.obligatorisk_gas,
                                data.gbsroles ?? []
                              )
                            : [],
                          baseSelection: {
                            id:
                              analys.grundurval_id != null
                                ? analys.grundurval_id
                                : undefined,
                            name:
                              analys.grundurval_namn != null
                                ? analys.grundurval_namn
                                : undefined,
                          },
                          detaljanalys: analys.detaljanalys ?? false,
                          ai_prompt: analys.ai_prompt ?? null,
                        };
                      }),
              };
            });

            selections = mergeSort(selections, (a, b) => {
              if (a.kombinerad && !b.kombinerad) {
                return -1;
              } else if (b.kombinerad && !a.kombinerad) {
                return 1;
              } else {
                return stringCompareNumeric(a.name, b.name);
              }
            });
          }
        }

        return props.children(
          selections,
          loading,
          data !== undefined && !error
        );
      }}
    </Query>
  );
};

export default EkonomiskAnalysListQuery;
